import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OpportunityCard from "@/components/OpportunityCard";
import { useSaved } from "@/lib/useSaved";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/Loaders";
import { scoreColor } from "@/components/ScoreMeter";
import {
  Lightbulb, FolderKanban, Bookmark, FileText, Sparkles, TrendingUp, Rocket,
  Newspaper, ArrowRight, Zap, Target, CheckCircle2,
} from "lucide-react";

const STAGES = ["idea", "validation", "mvp", "launch", "customer", "revenue"];
const STAGE_LABEL = { idea: "Idea", validation: "Validation", mvp: "MVP", launch: "Launch", customer: "First Customer", revenue: "Revenue" };

export default function Workspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { savedIds, toggleSave } = useSaved();
  const [brief, setBrief] = useState(null);
  const [saved, setSaved] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reports, setReports] = useState([]);
  const [forYou, setForYou] = useState([]);

  const loadAll = useCallback(() => {
    api.get("/brief").then((r) => setBrief(r.data)).catch(() => {});
    api.get("/saved").then((r) => setSaved(r.data.filter((i) => i.item_type === "opportunity" && i.data))).catch(() => {});
    api.get("/my/ideas").then((r) => setIdeas(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/my/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/feed/for-you").then((r) => setForYou(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const advanceStage = async (proj) => {
    const idx = STAGES.indexOf(proj.stage);
    const next = STAGES[Math.min(idx + 1, STAGES.length - 1)];
    await api.patch(`/projects/${proj.id}/stage`, { stage: next });
    loadAll();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-1">Builder Command Center</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-50">Welcome back, {user.name?.split(" ")[0]}</h1>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-sm text-slate-400"><Zap className="w-4 h-4 text-amber-400" /><span className="font-mono text-amber-400 font-bold">{user.xp || 0} XP</span></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Bookmark} label="Saved" value={saved.length} onClick={() => document.getElementById("ws-tabs")?.scrollIntoView({ behavior: "smooth" })} />
        <Stat icon={Lightbulb} label="Ideas Validated" value={ideas.length} />
        <Stat icon={FolderKanban} label="Projects" value={projects.length} />
        <Stat icon={FileText} label="AI Reports" value={reports.length} />
      </div>

      {brief && (
        <div className="glass rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4"><Newspaper className="w-5 h-5 text-cyan-400" /><h2 className="font-display text-xl font-bold text-slate-50">Your Daily Builder Brief</h2></div>
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
              {brief.recommended && (
                <Link to={`/opportunities/${brief.recommended.slug}`} className="rounded-xl bg-cyan-500/10 border border-cyan-500/25 p-4 hover:border-cyan-500/50 transition-colors">
                  <p className="text-[10px] uppercase font-mono text-cyan-400 mb-1 flex items-center gap-1"><Target className="w-3 h-3" />Recommended for you</p>
                  <p className="font-semibold text-slate-100 text-sm">{brief.recommended.title}</p>
                  <span className="font-mono text-xs" style={{ color: scoreColor(brief.recommended.overall_score) }}>Score {brief.recommended.overall_score}</span>
                </Link>
              )}
              {brief.underserved_market && (
                <Link to={`/opportunities/${brief.underserved_market.slug}`} className="rounded-xl bg-white/5 border border-white/10 p-4 hover:border-emerald-500/40 transition-colors">
                  <p className="text-[10px] uppercase font-mono text-emerald-400 mb-1">Underserved market</p>
                  <p className="font-semibold text-slate-100 text-sm">{brief.underserved_market.title}</p>
                  <span className="font-mono text-xs text-emerald-400">Low competition</span>
                </Link>
              )}
              <div className="rounded-xl bg-white/5 border border-white/10 p-4 sm:col-span-2">
                <p className="text-[10px] uppercase font-mono text-indigo-400 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" />Today's insight</p>
                <p className="text-sm text-slate-300">{brief.insight}</p>
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase font-mono text-slate-500 mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Rising trends</p>
              <div className="space-y-2">
                {brief.rising_trends?.map((t) => (
                  <Link key={t.name} to="/trends" className="flex items-center justify-between rounded-lg bg-white/5 border border-white/8 px-3 py-2 hover:border-cyan-500/40">
                    <span className="text-sm text-slate-300">{t.name}</span>
                    <span className="font-mono text-xs text-emerald-400">{t.trend_score}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {projects.length > 0 && (
        <div className="glass rounded-2xl p-6 mb-8">
          <h2 className="font-display text-xl font-bold text-slate-50 mb-5 flex items-center gap-2"><Rocket className="w-5 h-5 text-cyan-400" />Project Journey</h2>
          <div className="space-y-5">
            {projects.map((p) => {
              const idx = STAGES.indexOf(p.stage);
              return (
                <div key={p.id}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-100 text-sm">{p.title}</span>
                    {idx < STAGES.length - 1 && <Button size="sm" variant="ghost" onClick={() => advanceStage(p)} className="text-cyan-400 h-7 text-xs" data-testid={`advance-${p.id}`}>Advance <ArrowRight className="w-3 h-3 ml-1" /></Button>}
                  </div>
                  <div className="flex items-center gap-1">
                    {STAGES.map((st, i) => (
                      <div key={st} className="flex-1 flex flex-col items-center" data-testid={`builder-journey-step-${st}`}>
                        <div className={`w-full h-1.5 rounded-full ${i <= idx ? "bg-cyan-500" : "bg-white/10"}`} />
                        <span className={`text-[9px] mt-1 ${i <= idx ? "text-cyan-400" : "text-slate-600"}`}>{STAGE_LABEL[st]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div id="ws-tabs">
        <Tabs defaultValue="foryou">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="foryou" data-testid="tab-foryou">For You</TabsTrigger>
            <TabsTrigger value="saved" data-testid="tab-saved">Saved</TabsTrigger>
            <TabsTrigger value="ideas" data-testid="tab-ideas">Ideas</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">AI Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="foryou" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {forYou.map((o) => <OpportunityCard key={o.slug} opp={o} saved={savedIds.has(o.slug)} onSave={(x) => { toggleSave(x); }} />)}
            </div>
          </TabsContent>

          <TabsContent value="saved" className="mt-6">
            {saved.length === 0 ? <EmptyState icon={Bookmark} title="Nothing saved yet" subtitle="Save opportunities from the feed to track them here." action={<Link to="/discover"><Button className="bg-cyan-500 text-slate-950">Explore Opportunities</Button></Link>} /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{saved.map((i) => <OpportunityCard key={i.item_id} opp={i.data} saved onSave={(x) => { toggleSave(x); setTimeout(loadAll, 300); }} />)}</div>
            )}
          </TabsContent>

          <TabsContent value="ideas" className="mt-6">
            {ideas.length === 0 ? <EmptyState icon={Lightbulb} title="No validated ideas" subtitle="Run the Idea Validator to build your idea library." action={<Link to="/validate"><Button className="bg-cyan-500 text-slate-950">Validate an Idea</Button></Link>} /> : (
              <div className="space-y-3">{ideas.map((i) => (
                <Link key={i.id} to={`/reports/${i.report_id}`} className="glass rounded-xl p-4 flex items-center justify-between card-hover">
                  <span className="text-slate-200">{i.title}</span>
                  <span className="font-mono font-bold" style={{ color: scoreColor(i.overall) }}>{i.overall}</span>
                </Link>
              ))}</div>
            )}
          </TabsContent>

          <TabsContent value="reports" className="mt-6">
            {reports.length === 0 ? <EmptyState icon={FileText} title="No AI reports yet" subtitle="Analyze opportunities or validate ideas to generate reports." /> : (
              <div className="space-y-3">{reports.map((r) => (
                <Link key={r.id} to={`/reports/${r.id}`} className="glass rounded-xl p-4 flex items-center justify-between card-hover">
                  <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-400" /><span className="text-slate-200">{r.opportunity_title || r.title}</span></div>
                  <span className="text-xs font-mono uppercase text-slate-500">{r.type}</span>
                </Link>
              ))}</div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, onClick }) {
  return (
    <button onClick={onClick} className="glass rounded-xl p-5 text-left card-hover">
      <Icon className="w-5 h-5 text-cyan-400 mb-2" />
      <p className="font-mono text-2xl font-bold text-slate-50">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </button>
  );
}
